import { Route, Switch, useLocation, useHistory } from 'react-router-dom';
import { MARKETPLACE_DEVELOPER_STUDIO_ROUTES } from '../config/routes';
import { LoadingScreen } from 'features/common/components/LoadingScreen';
import { fetchLastSuccessfulLoginOrganizationId } from '../../common/auth/auth0Storage';
import { getBaseMarketplaceAppUrl } from '../config/env';
import { useFetchTenantsData } from '../core/auth/hooks/useFetchTenantsData';
import { IframeWithNavigationSync } from 'features/common/components/IframeWithNavigationSync';
import { useEffect } from 'react';

export const DeveloperRoutes: React.FC = () => {
  const location = useLocation();
  const history = useHistory();

  const { tenantsData, isLoading: tenantsLoading } = useFetchTenantsData();

  // Check if current path contains '/custom-app'
  const isCustomAppPath = location.pathname.includes('/custom-app');

  // Check if current path contains '/public-app'
  const isPublicAppPath = location.pathname.includes('/public-app');

  // Check if current path contains '/profile'
  const isProfilePage = location.pathname.includes('/profile');

  // Check if current path contains '/overview'
  const isInsightsPage = location.pathname.includes('/insights');

  // Check for appId in query parameters
  const searchParams = new URLSearchParams(location.search);
  const appId = searchParams.get('appId');
  const id = searchParams.get('id');
  const status = searchParams.get('status');

  // Remove appId from URL if we're on /developer but not /custom-app or /public-app
  useEffect(() => {
    if (!isCustomAppPath && !isPublicAppPath && (appId || id)) {
      // Remove appId from query parameters
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.delete('appId');
      newSearchParams.delete('id');

      // Construct new URL without appId
      const newSearch = newSearchParams.toString();
      const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;

      // Replace current URL without appId
      history.replace(newUrl);
    }
  }, [
    isCustomAppPath,
    isPublicAppPath,
    appId,
    id,
    location.search,
    location.pathname,
    history,
  ]);

  if (tenantsLoading) {
    return <LoadingScreen data-testid='loading-component' message='Loading…' />;
  }

  const marketplaceTenant = tenantsData[0]?.id;
  const organizationId = fetchLastSuccessfulLoginOrganizationId();
  const marketplaceAppUrl = getBaseMarketplaceAppUrl();

  // Construct the appropriate URL based on whether we're on custom-app path with appId or profile page/insights
  const getIframeUrl = () => {
    const baseParams = `organization=${organizationId}&tenantId=${marketplaceTenant}`;

    if (isProfilePage) {
      return `${marketplaceAppUrl}/app-studio/profile?${baseParams}`;
    }
    if (isInsightsPage) {
      return `${marketplaceAppUrl}/app-studio/insights?${baseParams}`;
    }
    if (isPublicAppPath && appId) {
      return `${marketplaceAppUrl}/app-studio/public-app?${baseParams}&appId=${appId}`;
    }
    if (isCustomAppPath && appId) {
      return `${marketplaceAppUrl}/app-studio/custom-app?${baseParams}&appId=${appId}`;
    }
    const statusParam = status ? `&status=${encodeURIComponent(status)}` : '';
    return `${marketplaceAppUrl}/app-studio?${baseParams}${statusParam}`;
  };

  return (
    <Route path={MARKETPLACE_DEVELOPER_STUDIO_ROUTES.ROOT}>
      <Switch>
        <Route
          path={[
            MARKETPLACE_DEVELOPER_STUDIO_ROUTES.ROOT,
            MARKETPLACE_DEVELOPER_STUDIO_ROUTES.CUSTOM_APP,
            MARKETPLACE_DEVELOPER_STUDIO_ROUTES.PUBLIC_APP,
            MARKETPLACE_DEVELOPER_STUDIO_ROUTES.PROFILE_PAGE,
            MARKETPLACE_DEVELOPER_STUDIO_ROUTES.INSIGHTS,
          ]}
          render={() => (
            <IframeWithNavigationSync
              src={getIframeUrl()}
              title='Marketplace Developer Iframe'
              allow='clipboard-read; clipboard-write'
              debugMode={process.env.NODE_ENV === 'development'}
            />
          )}
        />
      </Switch>
    </Route>
  );
};
